"use client";

import { useMemo } from "react";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { useQuery } from "@tanstack/react-query";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { useTrialStatus } from "@/app/_module/hooks/useTrialStatus";
import { subscriptionService } from "@/app/_module/services/subscriptionService";
import { usePhoneNumberValidation, type NoNumberReason } from "@/app/_module/hooks/usePhoneNumberValidation";
import { noNumberCopy, numbersSettingsPath } from "@/app/_module/components/NoActiveNumberNotice";
import { useTrialDialTime } from "@/app/_module/hooks/useTrialDialTime";
import { accountTypeHasTelephony } from "@/app/_module/components/TelephonyEnabled";
import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";
import { useDialCreditBalance } from "@/app/(authenticated)/settings/_modules/hooks/useDialCredits";
import { direct130CanDial, direct130RateLabel, type Direct130Dialing } from "@/app/(authenticated)/settings/_modules/services/dialCreditsService";

/**
 * Every reason an agent can't place a call right now, in priority order, with
 * the words to say and the way out. One hook so the dialer banner, the Start
 * buttons, the blocking dialog and the toasts can never disagree — and so the
 * agent is told the CAUSE (card declined, trial over, subscription ended, no
 * number) instead of watching a session start and stop.
 *
 * Mirrors what the backend actually enforces at call time:
 *  - past-due payment → feature_overrides.callingDisabled, numbers held
 *  - expired trial → dial-time cap 403 / viewer mode
 *  - ended subscription → viewer mode (read-only)
 *  - no active caller id → TwiML "no active phone number", call drops
 */
export type DialBlockerKind =
  | "account_suspended"
  | "plan_no_telephony"
  | "payment_past_due"
  | "trial_expired"
  | "trial_dial_time_exhausted"
  | "subscription_ended"
  | "number_trial_expired"
  | "number_subscription_expired"
  | "no_number"
  | "dial_credits_needed";

export interface DialBlocker {
  kind: DialBlockerKind;
  title: string;
  body: string;
  cta: string;
  href: string;
}

const shell = () => typeof window !== "undefined" && window.location.pathname.startsWith("/m");
export const billingPath = () => (shell() ? "/m/settings/subscription" : "/settings?tab=subscription");

const fmtDay = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;

const NUMBER_KIND: Record<NonNullable<NoNumberReason>, DialBlockerKind> = {
  trial_expired: "number_trial_expired",
  subscription_expired: "number_subscription_expired",
  never_purchased: "no_number",
};

export function dialBlockerCopy(
  kind: DialBlockerKind,
  ctx: {
    expired?: { phoneNumber: string; expiredAt: string | null } | null;
    trialEndedAt?: string | null;
    limitHours?: number;
    supportEmail?: string;
    dialing?: Direct130Dialing;
  } = {},
): DialBlocker {
  switch (kind) {
    case "dial_credits_needed": {
      const money = (n: number) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
      return {
        kind,
        title: "You're out of calling credits",
        body: `Calls are charged at cost (${ctx.dialing ? direct130RateLabel(ctx.dialing) : "$0.0130/min + $0.0065/call"}) until you have ${money(ctx.dialing?.threshold ?? 30000)} issued through Quote & Enroll in the last 30 days, then dialing is free. Add credits to keep dialing.`,
        cta: "Add credits",
        href: shell() ? "/m/settings/dial-credits" : "/settings?tab=dial-credits",
      };
    }
    case "account_suspended":
      return {
        kind,
        title: "This account is suspended",
        body: "Calling is off while the account is suspended. Contact support to get it restored.",
        cta: "Contact support",
        href: `mailto:${ctx.supportEmail || "support@insuracentral.com"}`,
      };
    case "plan_no_telephony":
      return {
        kind,
        title: "Your plan doesn't include calling",
        body: "Lite is the CRM without the dialers and calling: no dialer, no phone number, no texting. Upgrade to Basic to add the dialer and a local number, or Full-Fledged for multi-line.",
        cta: "Upgrade to Basic",
        href: billingPath(),
      };
    case "payment_past_due":
      return {
        kind,
        title: "Calling is paused: a payment didn't go through",
        body: "Your last payment couldn't be collected, so calls are on hold and your numbers are being kept for you. Update your card and calling comes back automatically.",
        cta: "Update payment method",
        href: billingPath(),
      };
    case "trial_expired": {
      const day = fmtDay(ctx.trialEndedAt);
      return {
        kind,
        title: "Your free trial has ended",
        body: `${day ? `Your trial ended on ${day}. ` : ""}Pick a plan to keep dialing — your leads, numbers and history are all still here.`,
        cta: "Choose a plan",
        href: billingPath(),
      };
    }
    case "trial_dial_time_exhausted":
      return {
        kind,
        title: `You've used your trial's ${ctx.limitHours ?? 20} dialing hours`,
        body: "Your trial's talk-time allowance is used up, so calls are paused. Pick a plan and the dialer turns back on with no cap. Your leads, numbers and history are all still here.",
        cta: "Choose a plan",
        href: billingPath(),
      };
    case "subscription_ended":
      return {
        kind,
        title: "Your subscription has ended",
        body: "Your account is read-only until it's reactivated. Restart your plan and the dialer turns back on right away.",
        cta: "Reactivate",
        href: billingPath(),
      };
    case "number_trial_expired":
    case "number_subscription_expired":
    case "no_number": {
      const reason: NoNumberReason =
        kind === "number_trial_expired" ? "trial_expired" : kind === "number_subscription_expired" ? "subscription_expired" : "never_purchased";
      const c = noNumberCopy(reason, ctx.expired || null);
      return { kind, title: c.title, body: c.body, cta: c.cta, href: numbersSettingsPath() };
    }
  }
}

export function useDialBlocker() {
  const { user } = useUserContext();
  const { branding } = useBranding();
  const u: any = user?.user || {};
  const trial = useTrialStatus();
  const numbers = usePhoneNumberValidation();
  const dialTime = useTrialDialTime();

  // Same query key as FailedPaymentBanner → one request, shared cache.
  const { data: subscription } = useQuery({
    queryKey: ["active-subscription"],
    queryFn: () => subscriptionService.getMyActiveSubscription(),
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled: !!u?.id,
  });

  const isEnterprise = u.accountType === "enterprise" || u.isEnterpriseAgency === true;
  const subStatus = String((subscription as any)?.status || u.subscriptionStatus || "").toLowerCase();

  // Direct 130 agents dial on credits; the backend refuses a metered one below a minute's charge (402).
  const onDirect130 = useDirect130Host();
  const { data: credits } = useDialCreditBalance({ enabled: onDirect130 && !!u?.id });
  const dialing = credits?.direct130Dialing ?? null;

  const blocker = useMemo<DialBlocker | null>(() => {
    // Nothing until the user record is here: the number check resolves before
    // the profile does, and judging "no number" against an unknown plan fired
    // a wrong toast (Lite has no number by design).
    if (!u.id || !u.accountType) return null;
    if (u.accountStatus === "suspended" || u.accountStatus === "inactive") return dialBlockerCopy("account_suspended", { supportEmail: branding.supportEmail });
    if (!accountTypeHasTelephony(u.accountType, u.trialAccountTypeUsed)) return dialBlockerCopy("plan_no_telephony");
    if (!isEnterprise) {
      if (["past_due", "unpaid", "incomplete"].includes(subStatus)) return dialBlockerCopy("payment_past_due");
      if (u.accountType === "trial" && (u.isTrialExpired === true || trial.isExpired) && !trial.hasPaidSubscription) {
        return dialBlockerCopy("trial_expired", { trialEndedAt: u.trialExpiresAt || trial.trialEndDate || null });
      }
      // Trial still running but its 20 talk-hours are gone. The backend refuses
      // manual and multi-line with a 403; the power dialer had no gate at all
      // and would dial into a drop — so stop it here, with the reason.
      if (dialTime.exhausted) {
        return dialBlockerCopy("trial_dial_time_exhausted", { limitHours: dialTime.limitHours });
      }
      if (
        !trial.isLoading &&
        !trial.isTrialActive &&
        !trial.hasPaidSubscription &&
        (trial.isViewerMode || ["canceled", "cancelled", "expired", "incomplete_expired"].includes(subStatus))
      ) {
        return dialBlockerCopy("subscription_ended");
      }
    }
    if (!numbers.isLoading && !numbers.hasActiveNumbers && numbers.noNumberReason) {
      return dialBlockerCopy(NUMBER_KIND[numbers.noNumberReason], { expired: numbers.expiredNumber });
    }
    if (dialing && !direct130CanDial(dialing)) return dialBlockerCopy("dial_credits_needed", { dialing });
    return null;
  }, [
    u.id, u.accountStatus, u.accountType, u.trialAccountTypeUsed, u.isTrialExpired, u.trialExpiresAt, isEnterprise, subStatus,
    trial.isExpired, trial.hasPaidSubscription, trial.isTrialActive, trial.isViewerMode, trial.isLoading, trial.trialEndDate,
    numbers.isLoading, numbers.hasActiveNumbers, numbers.noNumberReason, numbers.expiredNumber,
    dialTime.exhausted, dialTime.limitHours, branding.supportEmail, dialing,
  ]);

  return {
    blocked: !!blocker,
    blocker,
    // Pass-through so existing call sites keep their names.
    hasActiveNumbers: numbers.hasActiveNumbers,
    isCheckingPhoneNumbers: numbers.isLoading,
    noNumberReason: numbers.noNumberReason,
    expiredNumber: numbers.expiredNumber,
    recheck: numbers.revalidate,
  };
}
